import { detectOnlineMode, ONLINE_MODE, getTelemetryServerUrl } from '@/js/utils/environment.js';
import TelemetryEvent from '@/js/models/telemetryEvent.js';
import TelemetryBatch from '@/js/models/telemetryBatch.js';
import { safeUUID } from './util.js';
import { currentRouteTemplate } from './nav.js';

const APP_NAME = 'vitrina';
const MAX_BUFFER = 50;          // == server max_batch_len
const SESSION_KEY = 'telemetry_session_id';
const VISITOR_KEY = 'salon_visitor_id';

// ── Vitrina identity adaptation ──────────────────────────────────────────────
// app-salon-tools gates emission on the operator JWT (authenticated operators
// only) and the ingest service derives salon_id/staff_hash from the verified
// token. The vitrina is an ANONYMOUS consumer surface — there is no logged-in
// user and no JWT. Identity here is:
//   - visitor_id: a random UUID minted lazily on first emit and persisted in
//     localStorage under `salon_visitor_id`. Pure random — never derived from
//     anything, carries no PII, and links only telemetry events from one
//     browser profile to each other.
//   - salon_id: the salon this vitrina serves (window.__SALON_ID__, resolved by
//     the edge at boot). Public information — it IS the page being viewed —
//     but client-sent, so the server must treat it as unverified.
// Both are stamped into each event's free-form `props` (props keys are
// deliberately not aliased), so the wire schema is IDENTICAL to
// app-salon-tools' and no hand-invented alias enters the registry.

// ── Hardening caps (#1, #3) ───────────────────────────────────────────────────
// A single event over MAX_EVENT_BYTES would poison the buffer and trip a
// per-event 413 on the server; the body budget keeps a batch comfortably under
// sendBeacon's ~64 KB ceiling (the vitrina sends no `jw` token, so the
// BODY_BUDGET headroom only needs to cover the envelope — the cap is kept
// identical to app-salon-tools' for one shared server-side contract).
export const MAX_EVENT_BYTES = 4096;
// MAX_BODY_BYTES documents the raw sendBeacon/server ceiling; BODY_BUDGET is the enforced cap.
export const MAX_BODY_BYTES = 65536;
export const BODY_BUDGET = 60000;
export const MIN_FINAL_FLUSH_INTERVAL_MS = 5000;

/** UTF-8 byte length of a string (sendBeacon/413 limits are bytes, not chars). */
export const byteLen = (s) => new TextEncoder().encode(s).length;

let buffer = [];
let lastFinalAt = 0;
let droppedSinceFlush = 0;

function isOnline() { return detectOnlineMode() === ONLINE_MODE.ONLINE; }

/**
 * Ingest kill-switch. The salon-telemetry ingest has no anonymous path yet
 * (TELEMETRY.md open item), so a live vitrina would POST batches the ingest
 * rejects — quietly, since the edge proxy (same-origin, always 204) sits in
 * between, but a request per flush for zero data all the same. OFF unless the
 * build sets VITE_TELEMETRY_INGEST=on; flip it the moment the anonymous ingest
 * path lands (the transport already does). Read at call time so tests can stub
 * the env var per file.
 */
export function ingestEnabled() {
  try {
    return import.meta.env.VITE_TELEMETRY_INGEST === 'on';
  } catch {
    return false;
  }
}

// Vitrina: anonymous surface — no auth gate (app-salon-tools gates on the
// operator JWT here). Activation = kill-switch open + online.
function isActive() { return ingestEnabled() && isOnline(); }

/**
 * Anonymous visitor id: random UUID, minted lazily, persisted in localStorage
 * (`salon_visitor_id`). Returns null when localStorage is unavailable
 * (private-mode quota, disabled storage) — events then ship without a
 * visitor_id rather than failing.
 */
function visitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = safeUUID();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function sessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = safeUUID(); // crypto.randomUUID with a non-secure-context fallback (util.js)
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function appVersion() { return window.GLOBALversion || 'unknown'; }

function uaFamily(ua = '') {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
}

function deviceSummary() {
  let theme = 'light';
  let pwa = false;
  let net = null;
  try {
    theme = matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    pwa = matchMedia('(display-mode: standalone)').matches;
  } catch {
    // matchMedia absent or throws (SSR, non-browser env) — keep defaults
  }
  try {
    net = navigator.connection?.effectiveType || null;
  } catch {
    // navigator.connection absent — keep null
  }
  return {
    ua_family: uaFamily(navigator.userAgent),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    theme,
    pwa,
    net,
  };
}

/** Exported only for unit-test access — do not call in production code. */
export function _deviceSummaryForTest() { return deviceSummary(); }

/** Aliased-wire byte size of one TelemetryEvent. */
function eventBytes(event) {
  return byteLen(JSON.stringify(event.toAlias()));
}

/**
 * Identity props stamped on every event (vitrina adaptation — see the header
 * note). Small and fixed-size, so they survive the oversized-props trim.
 */
function identityProps() {
  const out = {};
  const vid = visitorId();
  if (vid) out.visitor_id = vid;
  try {
    if (window.__SALON_ID__) out.salon_id = String(window.__SALON_ID__);
  } catch {
    // window absent — skip salon attribution
  }
  return out;
}

/**
 * Buffer one telemetry event. Returns false (dropped) when offline — telemetry
 * is best-effort. Stamps the required per-event identity context (anonymous
 * visitor_id + salon_id on the vitrina).
 *
 * Blast-radius (#2): the entire body is wrapped — telemetry NEVER throws into
 * the host app. Any caught throw returns false (dropped, best-effort).
 * Byte cap (#1): an oversized event has its (only unbounded) `props` field
 * trimmed to the identity floor; if it still exceeds MAX_EVENT_BYTES it is dropped.
 */
export function emit({ event_type, name, route_template, trace_id, props, device_summary } = {}) {
  try {
    if (!isActive()) return false;
    // Stamp any accumulated drop count onto this event's props, then reset the
    // counter — so a silent buffer eviction becomes observable on the following
    // accepted event. The event that caused the eviction is already in the buffer;
    // the next call carries the signal.
    const pendingDropped = droppedSinceFlush;
    if (pendingDropped > 0) {
      droppedSinceFlush = 0;
    }
    const identity = identityProps();
    const mergedProps = pendingDropped > 0
      ? { ...props, ...identity, dropped: pendingDropped }
      : { ...props, ...identity };
    const base = {
      event_type,
      name,
      ts: Date.now(),
      app: APP_NAME,
      app_version: appVersion(),
      session_id: sessionId(),
      route_template: route_template || currentRouteTemplate(),
      trace_id,
      device_summary: device_summary || deviceSummary(),
    };
    let event = new TelemetryEvent({ ...base, props: mergedProps });
    if (eventBytes(event) > MAX_EVENT_BYTES) {
      // props is the only unbounded field (device_summary is fixed-small);
      // trim it to the identity floor and re-measure. Still over → drop the
      // poison event. Restore the pendingDropped counter so it rolls onto the
      // next accepted event — it must not be silently lost when the carrying
      // event is oversized.
      if (pendingDropped > 0) droppedSinceFlush += pendingDropped;
      event = new TelemetryEvent({ ...base, props: identity });
      if (eventBytes(event) > MAX_EVENT_BYTES) return false;
    }
    buffer.push(event);
    if (buffer.length > MAX_BUFFER) {
      buffer.shift(); // lossy by design
      droppedSinceFlush += 1;
    }
    return true;
  } catch {
    return false; // blast-radius guard: never throw into the app
  }
}

export function _getBuffer() { return buffer; }
export function _resetForTest() { stop(); buffer = []; lastFinalAt = 0; droppedSinceFlush = 0; }
/** Drain the buffer without resetting the drop counter (test-only). */
export function _resetBufferForTest() { buffer = []; }
/** Read the current drop counter (test-only). */
export function _droppedSinceFlushForTest() { return droppedSinceFlush; }

// ── Flush + lifecycle ────────────────────────────────────────────────────────

const SCHEMA_VERSION = 1;
const ENDPOINT_PATH = '/v1/events';
const FLUSH_INTERVAL_MS = 15000;
let timer = null;

/**
 * Greedily pack the front of `buffer` into a batch while staying under both the
 * count cap (MAX_BUFFER) and the serialized-body budget (BODY_BUDGET).
 *
 * Vitrina: unlike app-salon-tools there is no `jw` token variant — the batch
 * serializes identically on the steady fetch path and the final beacon path
 * (anonymous surface; the ingest auth model for unauthenticated visitors is an
 * OPEN ITEM tracked on vitrina-vue#19).
 *
 * Removes the selected events from `buffer` (the remainder waits for the next
 * flush). Safety: if zero events fit but the buffer is non-empty (shouldn't
 * happen — each event is ≤ MAX_EVENT_BYTES < BODY_BUDGET), drop one poison event
 * and return null so flush bails — never an infinite stall.
 *
 * @returns {{body: string}|null} the serialized batch body, or null (nothing to send)
 */
function packBatch() {
  let count = 0;
  let lastBody = null;
  while (count < buffer.length && count < MAX_BUFFER) {
    const events = buffer.slice(0, count + 1);
    const batch = new TelemetryBatch({ schema_version: SCHEMA_VERSION, events });
    const body = JSON.stringify(batch.toAlias());
    if (byteLen(body) > BODY_BUDGET) break;
    count += 1;
    lastBody = body;
  }
  if (count === 0) {
    // Poison event larger than the budget on its own — should be impossible
    // given the per-event cap, but never stall: drop it and bail.
    buffer.shift();
    return null;
  }
  buffer = buffer.slice(count);
  return { body: lastBody };
}

/**
 * Send buffered events. Best-effort: any failure drops the batch, never throws
 * into the app (#2). Offline → clear and bail (no retry, no replay).
 * `final` (pagehide/hidden) uses sendBeacon (text/plain, CORS-safelisted, no
 * preflight). On a `false` beacon return the events are DROPPED, not
 * requeued (#5): pagehide has no reliable next tick and `false` is ambiguous, so
 * requeue risks duplicates.
 *
 * Vitrina: no Authorization header and no body `jw` — the surface is anonymous.
 */
export async function flush({ final = false } = {}) {
  try {
    if (buffer.length === 0) return;
    if (!isOnline()) { buffer = []; return; }

    const packed = packBatch();
    if (!packed) return; // dropped a poison event; nothing to send this pass
    const { body } = packed;
    const url = getTelemetryServerUrl() + ENDPOINT_PATH;

    if (final) {
      const blob = new Blob([body], { type: 'text/plain' });
      navigator.sendBeacon(url, blob);
      // Drop on `false` (#5) — no requeue. Mark the final-flush timestamp so the
      // visibility debounce (#3) coalesces rapid tab-toggles.
      lastFinalAt = Date.now();
      return;
    }

    try {
      await fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      });
    } catch {
      /* network blip: drop this batch (best-effort) */
    }
  } catch {
    /* blast-radius guard: telemetry must never throw into the app (#2) */
  }
}

// Lifecycle listeners — each wrapped so a flush throw can never escape into the
// host page's event loop (#2).
function onVisibility() {
  try {
    if (document.visibilityState !== 'hidden') return;
    // Debounce rapid tab-toggles (consumers hopping back to Instagram/WhatsApp
    // in-app browsers) (#3).
    if (Date.now() - lastFinalAt < MIN_FINAL_FLUSH_INTERVAL_MS) return;
    flush({ final: true });
  } catch {
    /* swallow */
  }
}

function onPagehide() {
  try {
    // Terminal: always attempt (no-ops on an empty buffer). No debounce (#3).
    flush({ final: true });
  } catch {
    /* swallow */
  }
}

/** Start periodic + unload flushing. Idempotent. */
export function start() {
  if (!ingestEnabled()) return; // kill-switch: no timer, no listeners, no traffic
  if (timer) return;
  timer = setInterval(() => {
    try { flush(); } catch { /* swallow */ }
  }, FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPagehide);
}

/** Stop flushing + detach listeners (teardown / tests). */
export function stop() {
  if (timer) { clearInterval(timer); timer = null; }
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', onPagehide);
}
